import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DeviceSummary } from '../devices/useDevices'
import { MovieCard } from './MovieCard'
import type { LibraryMovieEntry } from './useLibrary'
import { useVirtualizedGrid } from './useVirtualizedGrid'
import { useGridKeyboardNav } from './useGridKeyboardNav'

interface MovieGridProps {
  entries: LibraryMovieEntry[]
  devices: Map<string, DeviceSummary>
  loading: boolean
  emptyMessage: string
  onOpenDetail: (tmdbId: number, type: 'movie') => void
  /** Controls whether the arrow-key navigation is live on this grid. */
  keyboardEnabled: boolean
}

/**
 * Virtualized library Movies grid.
 *
 * Renders library entries using `useVirtualizedGrid` so only rows
 * overlapping the current viewport (plus a small overscan) are mounted.
 * This matches the desktop `MovieGrid` + `useVirtualizedMovieGrid`
 * combo — cards that aren't on screen don't mount their
 * `useTmdbLazy` hooks, which keeps large libraries snappy and stays
 * inside TMDB rate limits.
 *
 * Behaviour notes:
 *
 *   - Columns are derived from the container width via a
 *     `ResizeObserver`, so resizing the window / collapsing a side
 *     sheet reflows the grid instantly.
 *   - The outer div sets `height = totalHeight`, making the document
 *     scrollbar reflect the full library even though only a handful
 *     of rows are rendered.
 *   - Sort changes preserve the focused card when the same tmdbId is
 *     still present in the sorted list — the focused index is
 *     recomputed from the previous tmdbId.
 *   - Enter / Space on the focused card opens the detail modal via
 *     `onOpenDetail`.
 *   - A small `scrollIntoView` side-effect keeps the focused card in
 *     the viewport as the user navigates with the keyboard.
 */
export function MovieGrid({
  entries,
  devices,
  loading,
  emptyMessage,
  onOpenDetail,
  keyboardEnabled,
}: MovieGridProps) {
  const itemCount = entries.length
  const { containerRef, layout, visibleRows } = useVirtualizedGrid({
    itemCount,
    gap: 16,
  })

  const [focusedIndex, setFocusedIndex] = useState(-1)
  const previousTmdbIdRef = useRef<number | null>(null)
  const focusedCardRef = useRef<HTMLDivElement | null>(null)

  // Preserve focus across sort / filter changes: if the previously
  // focused tmdbId still exists in the new list, move the focused
  // index to wherever it landed; otherwise clamp into bounds.
  useEffect(() => {
    if (itemCount === 0) {
      if (focusedIndex !== -1) setFocusedIndex(-1)
      previousTmdbIdRef.current = null
      return
    }
    const prevId = previousTmdbIdRef.current
    if (prevId != null) {
      const newIndex = entries.findIndex((e) => e.tmdbId === prevId)
      if (newIndex >= 0 && newIndex !== focusedIndex) {
        setFocusedIndex(newIndex)
        return
      }
    }
    if (focusedIndex >= itemCount) {
      setFocusedIndex(itemCount - 1)
    }
  }, [entries, itemCount, focusedIndex])

  // Track the currently focused tmdbId so the effect above can
  // restore focus after re-sorts.
  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < entries.length) {
      previousTmdbIdRef.current = entries[focusedIndex].tmdbId
    }
  }, [focusedIndex, entries])

  const getColumns = useCallback(() => layout.columns, [layout.columns])
  const handleActivate = useCallback(
    (index: number) => {
      const entry = entries[index]
      if (entry) onOpenDetail(entry.tmdbId, 'movie')
    },
    [entries, onOpenDetail]
  )

  useGridKeyboardNav({
    enabled: keyboardEnabled && !loading && itemCount > 0,
    itemCount,
    focusedIndex,
    setFocusedIndex,
    getColumns,
    onActivate: handleActivate,
  })

  // Scroll the focused card into view when it changes via keyboard.
  useEffect(() => {
    if (focusedIndex < 0) return
    const node = focusedCardRef.current
    if (!node) return
    node.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [focusedIndex])

  const rows = useMemo(() => {
    if (itemCount === 0 || layout.cardWidth === 0) return []
    return visibleRows.map((rowIndex) => {
      const cols: { entry: LibraryMovieEntry; index: number; col: number }[] = []
      for (let col = 0; col < layout.columns; col++) {
        const idx = rowIndex * layout.columns + col
        if (idx >= itemCount) break
        cols.push({ entry: entries[idx], index: idx, col })
      }
      return { rowIndex, cols }
    })
  }, [entries, itemCount, layout.columns, layout.cardWidth, visibleRows])

  if (loading) {
    return <p className="py-12 text-center text-text-tertiary">Loading…</p>
  }
  if (itemCount === 0) {
    return <p className="py-12 text-center text-text-tertiary">{emptyMessage}</p>
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height: layout.totalHeight }}
    >
      {rows.map(({ rowIndex, cols }) => (
        <div
          key={rowIndex}
          className="absolute left-0 right-0 flex"
          style={{
            top: rowIndex * layout.rowHeight,
            height: layout.cardHeight,
            gap: layout.gap,
          }}
        >
          {cols.map(({ entry, index }) => (
            <div
              key={entry.tmdbId}
              ref={index === focusedIndex ? focusedCardRef : undefined}
              style={{ width: layout.cardWidth, flex: '0 0 auto' }}
            >
              <MovieCard
                tmdbId={entry.tmdbId}
                record={entry.record}
                devices={devices}
                onOpen={onOpenDetail}
                focused={index === focusedIndex}
                onFocus={() => setFocusedIndex(index)}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
